import React, { Key, useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import { cloneDeep, isEqualWith } from "lodash";

import { RootState } from "us.helper/types";
import { $Skeleton, $Button, $Tree, $Popconfirm } from "us.common/components";
import * as Actions from "actions";
import { generateTreeData } from "components/TemplateEditor/Functions";
import { useField } from "formik";
import { useLocation } from "react-router";
import { getUrlParams } from "components/EditorHome/Functions";
import {
  getGroupIdByGroupName,
  getTemplateAdapterIds,
  getTemplateGroupName,
} from "functions";

type RepeatingAdapterChangerProps = {
  name?: string;
  isRepeating?: boolean;
  isSelect?: boolean;
  onClose?: () => void;
};

const RepeatingAdapterChanger: React.FC<
  PropsFromRedux & RepeatingAdapterChangerProps
> = (props) => {
  const {
    name = "adapter",
    isRepeating = true,
    isSelect = false,
    repeatingAdapters,
    template,
    templates,
    onClose,
    getRepeatingAdaptersIds,
    getRepeatingAdapterTokens,
    updateTemplate,
    getParameters,
  } = props;

  const { t } = useTranslation();
  const { search } = useLocation();
  const { groupId } = getUrlParams(search);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([]);
  const { groups } = templates ?? [];

  const [field, meta, helpers] = isSelect ? useField(name) : [];

  const groupName = useMemo(() => {
    return getTemplateGroupName(templates.groups, groupId);
  }, [groupId, templates.groups]);

  useEffect(() => {
    if (!template.loading && template.data) {
      getRepeatingAdaptersIds &&
        getRepeatingAdaptersIds({
          categoryId: template.data.categoryId,
          isRepeating: isRepeating,
          groupId: getGroupIdByGroupName(groupName, groups),
        });
      if (isRepeating) {
        setCheckedKeys(template.data.templateContent.adapter.repeatingAdapters);
      } else {
        field?.value && setCheckedKeys([field.value]);
      }
    }
  }, []);

  const treeData = useMemo(() => {
    return generateTreeData(repeatingAdapters.data);
  }, [repeatingAdapters]);

  const onLoadData = ({ key, children }: any) => {
    return new Promise<void>((resolve) => {
      if (children) {
        resolve();
        return;
      }
      setTimeout(() => {
        getRepeatingAdapterTokens && getRepeatingAdapterTokens([key]);
        resolve();
      }, 100);
    });
  };

  const handleUpdate = () => {
    const updatedTemplate = Object.assign(cloneDeep(template.data), {
      ...template.data,
      templateContent: {
        ...template.data.templateContent,
        adapter: {
          ...template.data.templateContent.adapter,
          repeatingAdapters: checkedKeys,
        },
      },
    });
    const adapterIds = getTemplateAdapterIds({
      template: updatedTemplate,
      all: true,
    });
    getParameters && getParameters(adapterIds);
    updateTemplate && updateTemplate(updatedTemplate);
    onClose && onClose();
  };

  return (
    <div className="repeating-adapter-changer">
      <$Skeleton
        loading={repeatingAdapters.isLoading}
        active
        paragraph={{
          rows: 2,
        }}
        className={repeatingAdapters.isLoading ? "px-3" : ""}
      >
        <$Tree
          checkable
          treeData={treeData}
          fieldNames={{
            title: "tokenName",
            key: "tokenValue",
            children: "dataTokens",
          }}
          loadData={onLoadData}
          expandedKeys={selectedKeys}
          checkedKeys={checkedKeys}
          onCheck={(checked: any, info: any) => {
            if (!checkedKeys.includes(info.node.adapterId)) {
              if (isRepeating) {
                setCheckedKeys([...checkedKeys, info.node.adapterId]);
              } else {
                setCheckedKeys([info.node.adapterId]);
                helpers?.setValue(info.node.adapterId);
              }
            } else {
              isRepeating &&
                setCheckedKeys(
                  checkedKeys.filter((key) => key != info.node.adapterId)
                );
            }
          }}
          defaultSelectedKeys={
            isRepeating
              ? template.data.templateContent.adapter.repeatingAdapters
              : undefined
          }
          onExpand={(expandedKeys: Key[]) => {
            setSelectedKeys(expandedKeys);
          }}
        />
      </$Skeleton>
      {!isSelect && (
        <div className="drawer-footer-fixed align-content-center justify-content-end">
          <$Popconfirm
            title={t("US.COMMON:COMMON.SURE_TO_PROCEED_?")}
            onConfirm={handleUpdate}
          >
            <$Button
              type="primary"
              className="mr-2"
              disabled={isEqualWith(
                checkedKeys,
                template.data.templateContent.adapter.repeatingAdapters
              )}
            >
              {t("COMMON.UPDATE")}
            </$Button>
          </$Popconfirm>
          {isEqualWith(
            checkedKeys,
            template.data.templateContent.adapter.repeatingAdapters
          ) && (
            <$Button onClick={() => onClose && onClose()}>
              {t("COMMON.CANCEL")}
            </$Button>
          )}

          {!isEqualWith(
            checkedKeys,
            template.data.templateContent.adapter.repeatingAdapters
          ) && (
            <$Popconfirm
              title={t(
                "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.REPEATING_ADAPTERS_CHANGES_HAVE_NOT_BEEN_COMPLETED_AND_YOUR_CHANGES_WILL_NOT_BE_ADDED_TO_THE_TEMPLATE,DO_YOU_WISH_TO_CONTINUE_?"
              )}
              placement="topLeft"
              onConfirm={() => onClose && onClose()}
              okText={t("COMMON.OK")}
              cancelText={t("COMMON.NO")}
              className="mr-2"
            >
              <$Button>{t("COMMON.CANCEL")}</$Button>
            </$Popconfirm>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor, templatesHistory } = state;
  const { parameters, section, repeatingAdapters } = templateEditor;
  const { standard, repeating } = parameters;
  const { template, templates } = templatesHistory;

  return {
    standardParameters: standard,
    repeatingParameters: repeating,
    section,
    template,
    templates,
    repeatingAdapters,
  };
};

const { parameters, repeatingAdapterTokens, section, repeatingAdapters } =
  Actions.templateEditor;
const { template } = Actions.templatesHistory;

const mapDispatchToProps = {
  updateTemplate: template.update,
  getRepeatingAdaptersIds: repeatingAdapters.get,
  getRepeatingAdapterTokens: repeatingAdapterTokens.get,
  getParameters: parameters.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(RepeatingAdapterChanger);
